// Added by Sejune Oh on 2019-09-04
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component } from 'react'
import { observer, inject, disposeOnUnmount } from 'mobx-react'
import { computed, reaction, observable, action, makeObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Intl from '@/helper/Intl'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import NotiBar from './NotiBar'
import { UserAuthStore } from '@/stores/user/UserAuth'
import { ConnectionStore, ResServerStatusProps, ServerConnectionProps, VersionInfoProps } from '@/stores/global'
import EventStore from '@/stores/EventStore'
import ProductStore from '@/stores/ProductStore'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface NotiBarCProps {
  userAuth?: UserAuthStore
  connection?: ConnectionStore
  globalEvt?: EventStore
  product?: ProductStore
}

export enum NotiLevel {
  'none',
  'info',
  'warn',
  'err',
}

const MAX_RETRY_COUNT = 3

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'connection', 'globalEvt', 'product')
@observer
class NotiBarContainer extends Component<NotiBarCProps> {
  _isListening: boolean = false
  _notiLevl: NotiLevel = NotiLevel.none
  _notiMsg: string = ''
  _notiLink?: string
  _packagePath?: string
  _retryCount: number = 0

  constructor(props: NotiBarCProps) {
    super(props)
    makeObservable(this, {
      _isListening: observable,
      _notiLevl: observable,
      _notiMsg: observable,
      _notiLink: observable,
      _packagePath: observable,
      _retryCount: observable,
      componentDidMount: action,
      _listenServerEventStream: action,
      _handleConnected: action,
      _handleProessResponse: action,
      _handleComplete: action,
      _handleError: action,
      _reconnect: action,
      _setNotiInfoWithServerConnection: action,
      _setNotiInfoWithVersion: action,
      _setPackagePath: action,
      _setNotiInfoEmpty: action,
      _setErrMessage: action,
      _handleCloseBtnClick: action,
      userAuth: computed,
      connection: computed,
      globalEvt: computed,
      product: computed,
      isElectronViewer: computed,
      updateEnabled: computed,
    })
  }

  componentDidMount() {
    disposeOnUnmount(this, [
      reaction(
        () => this.userAuth.isSignedUp,
        (signedStatus) => {
          if (signedStatus) {
            this._listenServerEventStream()
          } else {
            this._setNotiInfoEmpty()
            this._isListening = false
          }
        }
      ),
    ])

    if (this.userAuth.isSignedUp) this._listenServerEventStream()
  }

  render() {
    if (!this.userAuth.isSignedUp || !this._isListening) return null
    return (
      <NotiBar
        notiLevel={this._notiLevl}
        notiMsg={this._notiMsg}
        notiLink={this._notiLink}
        onLinkClick={this._handleLinkClick}
        onCloseBtnClick={this._handleCloseBtnClick}
      />
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get userAuth() {
    return this.props.userAuth!
  }

  get connection() {
    return this.props.connection!
  }

  get globalEvt() {
    return this.props.globalEvt!
  }

  get product() {
    return this.props.product!
  }

  get isElectronViewer() {
    // @ts-ignore
    return !!window.process
  }

  get updateEnabled() {
    const hostName = window.location.hostname
    if ((hostName === 'localhost' || hostName === '127.0.0.1') && this.isElectronViewer) return true
    return false
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  _listenServerEventStream = () => {
    if (this._isListening) return

    this._isListening = true
    this.connection.bindServerEventWithListener(
      this._handleProessResponse,
      this._handleConnected,
      this._handleComplete,
      this._handleError
    )
  }

  _handleConnected = () => {
    this._retryCount = 0
    this._setNotiInfoEmpty()
  }

  _handleProessResponse = ({ result, err }: ResServerStatusProps) => {
    if (err) {
      this._setErrMessage(err)
      return
    }

    try {
      const { aview_server_connection, new_version } = result!.system_status!

      if (new_version && new_version.has_new_version) {
        this._setNotiInfoWithVersion(new_version)
        this._setPackagePath(new_version)
      }

      if (aview_server_connection) {
        if (aview_server_connection.connected!) {
          if (this._notiLevl === NotiLevel.info) return

          this._setNotiInfoEmpty()
          return
        }

        this._setNotiInfoWithServerConnection(aview_server_connection)
      }
    } catch (err) {}
  }

  _handleComplete = (result: any) => {
    this._isListening = false
  }

  _handleError = (err: any) => {
    if (this._retryCount >= MAX_RETRY_COUNT) {
      this._setErrMessage(err)
      return
    }

    this._retryCount = this._retryCount + 1
    this._reconnect()
  }

  _reconnect = () => {
    this.connection.destroyConnection!()

    // 1초 후 다시 연결
    setTimeout(() => {
      this._isListening = false
      Logger.networkLog(`Reconnect server (${this._retryCount} / ${MAX_RETRY_COUNT})`)
      this._listenServerEventStream()
    }, 1000)
  }

  _setNotiInfoWithServerConnection = ({ connected, name, endpoint }: ServerConnectionProps) => {
    const notiMsg = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.msg_check_network' })
    this._notiLevl = NotiLevel.err
    this._notiMsg = notiMsg
    this._notiLink = undefined
    Logger.errLog(`Lost server connection. ${endpoint} / ${connected}`)
  }

  _setNotiInfoWithVersion = ({ version }: VersionInfoProps) => {
    const updateMsg = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.msg_update_version' })
    const installMsg = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.msg_install' })
    this._notiLevl = NotiLevel.info
    this._notiMsg = updateMsg
    this._notiLink = this.updateEnabled ? `${installMsg} ${version}` : undefined
  }

  _setPackagePath = ({ filepath }: VersionInfoProps) => {
    this._packagePath = filepath
  }

  _setNotiInfoEmpty = () => {
    this._notiLevl = NotiLevel.none
    this._notiMsg = ''
    this._notiLink = undefined
  }

  _setErrMessage = (err: any) => {
    const defaultErrMessage = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.msg_default_err' })
    this._notiLevl = NotiLevel.err
    this._notiMsg = defaultErrMessage
    this._notiLink = undefined
    Logger.errLog('Get error from server status stream')
    console.log(err)
  }

  _handleCloseBtnClick = () => {
    this._notiLevl = NotiLevel.none
    this._notiMsg = ''
    this._notiLink = undefined
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleLinkClick = () => {
    const title = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.dialog_title_update_version' })
    const description = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.dialog_desc_update_version' })
    const confirmBtn = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.dialog_btn_confirm' })
    const cancelBtn = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.dialog_btn_cancel' })
    this.globalEvt.openDialog(
      {
        title,
        description: description + (this._notiLink ? ` (${this._notiLink})` : ''),
        confirmBtn,
        cancelBtn,
      },
      () => {
        return new Promise(async (resolve) => {
          this._handleCloseBtnClick()
          try {
            this._installNewPackage(this._notiLink!)
          } catch (err) {
            console.log('Fail to install')
            console.log(err)
          }

          resolve(true)
        })
      }
    )
  }

  _installNewPackage = (version?: string) => {
    if (!this.isElectronViewer) return

    if (!this._packagePath && this._packagePath === '') {
      const err = Intl.getIntlText({ id: 'htmljs_react.component.notibarcontainer.msg_filepath_not_found' })
      this._setErrMessage(err)
      return
    }

    // eslint-disable-next-line no-undef
    const { shell, ipcRenderer } = __non_webpack_require__('electron')

    // open folder
    shell.showItemInFolder(this._packagePath)

    // viewer를 강제 종료한다.
    setTimeout(() => {
      ipcRenderer.send('forced-exit', {})
    }, 2000)
  }
}

export default NotiBarContainer
